import * as React from "react";
import { scaleOrdinal, transition, select, arc, pie, interpolate } from "d3";
import { ChartLegend } from "../chart-legend/chart-legend.mjs";
import { Heading } from "../../typography/heading/heading.mjs";
import { Tooltip } from "../../tooltip/tooltip.mjs";
const _ChartPie = class _ChartPie extends React.Component {
  constructor(props) {
    super(props);
    this.containerWidth = 0;
    this.innerRadius = 0;
    this.outerRadius = 0;
    this.unusedInnerRadius = 0;
    this.unusedOuterRadius = 0;
    this.mounted = false;
    this.graphCreated = false;
    this.resize = () => {
      this.updateGraph();
    };
    this.state = {
      colors: null,
      arcRefs: [],
      groupArcRef: null
    };
  }
  componentDidMount() {
    this.mounted = true;
    const colors = scaleOrdinal(this.props.colors);
    this.setState({ colors }, () => {
      this.createGraph();
      window.addEventListener("resize", this.resize);
    });
  }
  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener("resize", this.resize);
  }
  componentDidUpdate(prevProps) {
    if (!this.compareData(prevProps.data, this.props.data) && this.mounted && this.graphCreated) {
      this.updateGraph();
    }
  }
  BEM() {
    const classArray = ["chart-pie"];
    classArray.push("chart-pie--align-" + this.props.align);
    if (this.props.legend) {
      classArray.push("chart-pie--legend");
      if (this.props.legend.aside) {
        classArray.push("chart-pie--legend-aside");
      }
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    return classArray.join(" ");
  }
  /* Graph */
  createGraph() {
    transition().duration(800).ease(this.easeOutQuint);
    this.svgEl = select(this.containerEl).insert("svg", ":first-child").attr("class", "chart-pie__graph-svg");
    this.unusedGroupEl = this.svgEl.append("g").attr("class", "chart-pie__unused");
    this.graphGroupEl = this.svgEl.append("g").attr("class", "chart-pie__graph");
    this.tooltipGroupEl = this.svgEl.append("g").attr("class", "chart-pie__tooltip");
    this.unusedGroupEl.append("path").attr("class", "chart-pie__unused-path");
    this.graphCreated = true;
    this.updateGraph();
  }
  updateGraph() {
    const { arcWidth, arcUnusedWidth } = this.props;
    const data = this.props.disabled ? [] : this.props.data.json;
    this.containerWidth = this.containerEl.clientWidth;
    this.svgEl.attr("width", this.containerWidth).attr("height", this.containerWidth);
    this.unusedGroupEl.attr("transform", this.alignCenter());
    this.graphGroupEl.attr("transform", this.alignCenter());
    this.tooltipGroupEl.attr("transform", this.alignCenter());
    this.innerRadius = this.containerWidth / 2 - arcWidth;
    this.outerRadius = this.containerWidth / 2;
    this.unusedInnerRadius = (this.containerWidth - arcUnusedWidth - arcWidth) / 2;
    this.unusedOuterRadius = (this.containerWidth + arcUnusedWidth - arcWidth) / 2;
    this.updateGraphUnusedPath();
    this.updateGraphClipPath(data);
    this.updateGraphArcPath(data);
    this.updateGraphTooltipArc(data);
  }
  updateGraphUnusedPath() {
    const unusedArc = arc().innerRadius(this.unusedInnerRadius).outerRadius(this.unusedOuterRadius).startAngle(0).endAngle(Math.PI * 2);
    this.unusedGroupEl.select(".chart-pie__unused-path").attr("d", unusedArc);
  }
  updateGraphClipPath(data) {
    const { arcWidth } = this.props;
    const totalUsed = this.getTotal(data);
    const totalUnused = this.getTotalUnused(data);
    const clipArc = arc().innerRadius(this.innerRadius).outerRadius(this.outerRadius).cornerRadius(arcWidth / 2);
    const clipData = pie().sort(null)([totalUsed, totalUnused]);
    const clipPath = this.graphGroupEl.selectAll(".chart-pie__clip").data(clipData);
    clipPath.exit().remove();
    clipPath.select(".chart-pie__clip-path").interrupt().transition().attr("d", clipArc);
    clipPath.enter().append("clipPath").attr("class", "chart-pie__clip").attr("id", (_, i) => this.props.id + "-" + i).append("path").attr("class", "chart-pie__clip-path").transition().delay(320).attrTween("d", (d) => this.arcTween(d, clipArc));
  }
  updateGraphArcPath(data) {
    const self = this;
    const arcDatum = arc().innerRadius(this.innerRadius).outerRadius(this.outerRadius);
    const arcData = pie().sort(null).value(({ val }) => val)(data);
    const arcPath = this.graphGroupEl.selectAll(".chart-pie__arc").data(arcData);
    arcPath.exit().remove();
    arcPath.interrupt().transition().attr("d", arcDatum);
    arcPath.enter().append("path").attr("class", "chart-pie__arc").attr("clip-path", () => "url(#" + this.props.id + "-0)").style("fill", (d) => this.getColor(d.data.key)).style("visibility", (d) => d.data.key === this.props.data.unused ? "hidden" : "visible").transition().delay(320).attrTween("d", (d) => this.arcTween(d, arcDatum));
    self.setState({
      arcRefs: this.graphGroupEl.selectAll(".chart-pie__arc").nodes()
    });
  }
  updateGraphTooltipArc(data) {
    var _a;
    const self = this;
    const total = ((_a = this.props.tooltip) == null ? void 0 : _a.grouped) ? [this.getTotal(data, true)] : [];
    const tooltipArc = arc().innerRadius(0).outerRadius(this.outerRadius);
    const tooltipArcData = pie().sort(null)(total);
    const tooltipArcPath = this.tooltipGroupEl.selectAll(".chart-pie__tooltip-arc").data(
      tooltipArcData
    );
    tooltipArcPath.exit().remove();
    tooltipArcPath.attr("d", tooltipArc);
    tooltipArcPath.enter().append("path").attr("class", "chart-pie__tooltip-arc").attr("d", tooltipArc).style("fill", () => "transparent").attr("d", tooltipArc);
    self.setState({ groupArcRef: tooltipArcPath.node() });
  }
  compareData(o1, o2) {
    if (typeof o1 === "undefined" || o1 === null || typeof o2 === "undefined" || o2 === null) {
      return o1 === o2;
    }
    return typeof o1 === "object" && Object.keys(o1).length > 0 ? Object.keys(o1).length === Object.keys(o2).length && Object.keys(o1).every((p) => this.compareData(o1[p], o2[p])) : o1 === o2;
  }
  /* Title */
  getTitle() {
    return /* @__PURE__ */ React.createElement("div", { className: "chart-pie__title" }, /* @__PURE__ */ React.createElement(Heading, { level: this.props.headingLevel, className: "h3 chart-pie__title-text" }, this.props.title));
  }
  /* Legend */
  getLegend() {
    const { format } = this.props;
    if (!this.props.legend) {
      return null;
    }
    const items = this.props.data.json.map((item) => {
      const keyFormatted = this.getFormattedKey(item.key);
      const valFormatted = this.getFormattedVal(item.val);
      return {
        color: this.getColor(item.key),
        key: item.key,
        text: keyFormatted,
        val: item.val,
        valFormatted
      };
    });
    return /* @__PURE__ */ React.createElement("div", { className: "chart-pie__legend" }, /* @__PURE__ */ React.createElement(ChartLegend, { items, format: format.legend, stacked: this.props.legend.stacked, size: this.props.legend.size }));
  }
  /* Tooltip */
  getTooltip() {
    var _a;
    if (this.props.disabled) {
      return null;
    }
    if (!((_a = this.props.tooltip) == null ? void 0 : _a.show)) {
      return null;
    }
    const tooltipProps = this.getTooltipSettings();
    const items = this.props.data.json.map((item) => {
      var _a2;
      const keyFormatted = this.getFormattedKey(item == null ? void 0 : item.key);
      const valFormatted = this.getFormattedVal(item == null ? void 0 : item.val);
      const keyColor = this.getColor(item == null ? void 0 : item.key);
      if ((_a2 = this.props.format) == null ? void 0 : _a2.tooltip) {
        return {
          color: keyColor,
          key: item.key,
          text: this.props.format.tooltip(item.key, keyFormatted, item.val, valFormatted, keyColor),
          val: item.val,
          valFormatted
        };
      }
      return {
        color: keyColor,
        key: item.key,
        text: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("strong", null, valFormatted), " ", keyFormatted),
        val: item.val,
        valFormatted
      };
    });
    if (!this.props.tooltip.grouped) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, this.state.arcRefs.map(
        (ref, index) => ref && /* @__PURE__ */ React.createElement(Tooltip, { key: this.props.data.json[index].key, ...tooltipProps, referenceElement: ref }, /* @__PURE__ */ React.createElement(ChartLegend, { items: [items[index]], stacked: true }))
      ));
    }
    return /* @__PURE__ */ React.createElement(Tooltip, { ...tooltipProps, referenceElement: this.state.groupArcRef }, /* @__PURE__ */ React.createElement(ChartLegend, { items, stacked: true }));
  }
  getTooltipSettings() {
    if (!this.props.tooltip) {
      return null;
    }
    const placement = "right";
    const customSettings = this.props.tooltip.settings ? this.props.tooltip.settings : {};
    return {
      placement,
      ...customSettings
    };
  }
  /* D3 helpers */
  arcTween(d, arc2) {
    const interpolateFn = interpolate({ endAngle: 0, startAngle: 0 }, d);
    return (t) => {
      var _a;
      return (_a = arc2(interpolateFn(t))) != null ? _a : "";
    };
  }
  getColor(key) {
    return key === this.props.data.unused ? "var(--color-neutral-bg-subtle, #e4e4e4)" : this.state.colors(key);
  }
  getTotal(arr, withUnused = false) {
    return arr.filter((item) => withUnused || item.key !== this.props.data.unused).map((item) => item.val).reduce((a, b) => a + b, 0);
  }
  getTotalUnused(arr) {
    if (!this.props.data.unused) {
      return 0;
    }
    return arr.filter((item) => item.key === this.props.data.unused).map((item) => item.val)[0] || 0;
  }
  /* CSS helpers */
  alignCenter() {
    return "translate(" + this.containerWidth / 2 + "," + this.containerWidth / 2 + ")";
  }
  /* Format */
  getFormattedKey(key) {
    const { format } = this.props;
    return this.getFormatted(format.key, key);
  }
  getFormattedVal(val) {
    const { format } = this.props;
    return this.getFormatted(format.val, val);
  }
  getFormatted(format, val) {
    if (format) {
      if (typeof format === "object" && !(format instanceof Array)) {
        return format[val];
      }
      if (typeof format === "function" && !(format instanceof Array)) {
        return format(val);
      }
      return "".concat(val, " ").concat(format);
    }
    return val.toString();
  }
  /* Transitions */
  easeOutQuint(t) {
    return 1 + --t * t * t * t * t;
  }
  /* Render */
  render() {
    const { arcWidth, contentPadding } = this.props;
    const padding = arcWidth + contentPadding;
    return /* @__PURE__ */ React.createElement("div", { className: this.BEM() }, /* @__PURE__ */ React.createElement("div", { className: "chart-pie__inner" }, this.props.title && this.getTitle(), /* @__PURE__ */ React.createElement("div", { className: "chart-pie__main" }, /* @__PURE__ */ React.createElement("div", { className: "chart-pie__graph-container", ref: (containerEl) => this.containerEl = containerEl, style: { maxWidth: this.props.maxWidth } }, /* @__PURE__ */ React.createElement("div", { style: { width: this.props.maxWidth } }), /* @__PURE__ */ React.createElement(
      "div",
      {
        className: "chart-pie__graph-content",
        style: {
          padding,
          // Clippath makes content round so it does not get on the graph and ensures graph tooltips are working
          clipPath: "circle(calc(50% - ".concat(padding, "px))")
        }
      },
      /* @__PURE__ */ React.createElement("div", { className: "chart-pie__graph-content-inner" }, this.props.children)
    )), this.state.colors && this.getLegend(), this.state.colors && this.getTooltip())));
  }
};
_ChartPie.defaultProps = {
  align: "left",
  arcUnusedWidth: 6,
  arcWidth: 6,
  children: [],
  colors: [
    "var(--color-chart-1)",
    "var(--color-chart-2)",
    "var(--color-chart-3)",
    "var(--color-chart-4)",
    "var(--color-chart-5)",
    "var(--color-chart-6)",
    "var(--color-chart-7)"
  ],
  contentPadding: 10,
  format: {
    key: void 0,
    legend: void 0,
    tooltip: void 0,
    val: void 0
  },
  maxWidth: 240,
  headingLevel: 3
};
let ChartPie = _ChartPie;
export {
  ChartPie
};
